/**
 * Persistance locale des personnages via IndexedDB.
 *
 * API simple : list, get, save, remove.
 * Les personnages sont stockes dans un object store "personnages" indexe par id (UUID).
 */

import type { Personnage } from '@domain/personnage';

const DB_NAME = 'cof2-personnages';
const DB_VERSION = 9;
const STORE = 'personnages';

function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (db.objectStoreNames.contains(STORE)) {
        db.deleteObjectStore(STORE);
      }
      db.createObjectStore(STORE, { keyPath: 'id' });
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

export async function listPersonnages(): Promise<Personnage[]> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, 'readonly');
    const store = tx.objectStore(STORE);
    const req = store.getAll();
    req.onsuccess = () => resolve(req.result as Personnage[]);
    req.onerror = () => reject(req.error);
  });
}

export async function getPersonnage(id: string): Promise<Personnage | undefined> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, 'readonly');
    const store = tx.objectStore(STORE);
    const req = store.get(id);
    req.onsuccess = () => resolve(req.result as Personnage | undefined);
    req.onerror = () => reject(req.error);
  });
}

export async function savePersonnage(p: Personnage): Promise<void> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, 'readwrite');
    const store = tx.objectStore(STORE);
    store.put(p);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

export async function removePersonnage(id: string): Promise<void> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, 'readwrite');
    const store = tx.objectStore(STORE);
    store.delete(id);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

export function exportPersonnageJSON(p: Personnage): void {
  const json = JSON.stringify(p, null, 2);
  const blob = new Blob([json], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${p.nom.replace(/\s+/g, '_')}_niv${p.niveau}.json`;
  a.click();
  URL.revokeObjectURL(url);
}

export async function importPersonnageJSON(file: File): Promise<Personnage> {
  const text = await file.text();
  const data = JSON.parse(text) as Personnage;
  const imported: Personnage = { ...data, id: crypto.randomUUID() };
  await savePersonnage(imported);
  return imported;
}

export async function duplicatePersonnage(id: string): Promise<Personnage | null> {
  const original = await getPersonnage(id);
  if (!original) return null;
  const copy: Personnage = {
    ...original,
    id: crypto.randomUUID(),
    nom: `${original.nom} (copie)`,
  };
  await savePersonnage(copy);
  return copy;
}
